import { Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import Spinner from '~/components/Spinner';
import {
  MEDIA_PACK_ENTITY_QUERY_KEY,
  YOUTUBE_CHANNEL_INSIGHTS_QUERY_KEY,
  YT_CONTENTS_COLLECTION_QUERY_KEY,
} from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import { CreatorBenchmarks, DefaultMediaPackType, YoutubeType } from '~/types';

import { patchMediaPack } from '../../MediaPackDetailDefaultScreen.services';
import { UpdateContentSubmitValues } from '../MediaPackDetailSelectContentModal/MediaPackDetailSelectContentModal.types';
import { YoutubeSection } from './YoutubeSection';
import {
  getYoutubeContents,
  getYoutubeInsights,
} from './YoutubeSection.services';

interface Props {
  mediaPack: DefaultMediaPackType;
  creatorBenchmarks: CreatorBenchmarks;
  isSharedView?: boolean;
}

export const YouTubeSectionContainer: React.FC<Props> = ({
  mediaPack,
  creatorBenchmarks,
  isSharedView,
}) => {
  const queryClient = useQueryClient();
  const creator = useSelector(selectCreator);
  const router = useRouter();
  const { sharedToken } = router.query;

  const {
    data: ytInsights,
    isLoading,
    error,
  } = useQuery(
    [
      YOUTUBE_CHANNEL_INSIGHTS_QUERY_KEY,
      creator?.ytChannels?.[0]?.id,
      sharedToken,
    ],

    () =>
      getYoutubeInsights(
        mediaPack?.id,
        sharedToken as string | undefined,
        creator?.id,
      ),
    { enabled: !!creator?.ytChannels?.[0]?.id },
  );

  const {
    data: ytContents = [],
    isLoading: isYoutubeContentLoading,
    error: youtubeContentError,
  } = useQuery<YoutubeType[]>(
    [YT_CONTENTS_COLLECTION_QUERY_KEY],
    () =>
      getYoutubeContents(
        mediaPack?.ytContents?.reduce((acc, { id }) => {
          if (id) {
            acc.push(id);
          }
          return acc;
        }, []),
        sharedToken as string | undefined,
        creator?.id,
      ),
    { keepPreviousData: true },
  );

  const handleUpdateMediaPack = async (values: UpdateContentSubmitValues) => {
    await patchMediaPack({
      id: mediaPack?.id,
      data: {
        ytContents: values.contents,
      },
      _impersonateOnce: creator?.id,
    });

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY],
      }),
      queryClient.invalidateQueries({
        queryKey: [YT_CONTENTS_COLLECTION_QUERY_KEY],
      }),
    ]);
  };

  const topSixYtContents = ytContents.slice(0, 6);

  if (isLoading || isYoutubeContentLoading) {
    return <Spinner centered aria-label="Loading data" />;
  }

  if (error || youtubeContentError) {
    <Text color="error.500">Something went wrong.</Text>;
  }

  return (
    <YoutubeSection
      creatorBenchmarks={creatorBenchmarks}
      mediaPack={mediaPack}
      ytContents={topSixYtContents}
      ytInsights={ytInsights}
      onUpdateMediaPack={handleUpdateMediaPack}
      isSharedView={isSharedView}
    />
  );
};
